import { useState, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, Sparkles, Send, AlertTriangle, HelpCircle, Search, ShieldAlert,
} from 'lucide-react';
import DeliveryBadge from './DeliveryBadge';

const API = '/api';

// COMPOSE — one email, one person, in Randall's voice.
//
// The rule this screen exists to hold: nothing is auto-sent, and nothing is ever described
// as delivered on the record's own claim. A send that has left draft but is not confirmed
// says "dispatched, not yet confirmed" — because that is what we know.

// Mirrors compose_api._PLACEHOLDER. Kept client-side ON PURPOSE, in addition to the server
// guard, so the button is DISABLED and says why rather than the user hitting send and
// getting a 400. The server check is still the one that counts; this one is courtesy.
const PLACEHOLDER = /\[[A-Z][A-Z0-9_ ]{1,30}:/;
const CS_MARKER = /\[CS:|\[!\]/i;

const day = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return String(iso).slice(0, 10); }
};

export default function Compose({ initialEmail = '' }) {
  const [email, setEmail] = useState(initialEmail);
  const [name, setName] = useState('');
  const [who, setWho] = useState(null);
  const [loadingWho, setLoadingWho] = useState(false);
  const [intent, setIntent] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sent, setSent] = useState(null);

  const lookUp = useCallback(async () => {
    const e = email.trim().toLowerCase();
    if (!e.includes('@')) { setErr('Enter a full email address.'); return; }
    setLoadingWho(true); setErr(null); setSent(null);
    try {
      const r = await axios.get(`${API}/compose/recipient`, { params: { email: e } });
      setWho(r.data);
      if (r.data.name && !name) setName(r.data.name);
    } catch (ex) { setErr(ex?.response?.data?.detail || ex.message); setWho(null); }
    setLoadingWho(false);
  }, [email, name]);

  const draft = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await axios.post(`${API}/compose/draft`, {
        email: email.trim().toLowerCase(), name, intent, subject,
      });
      setDraftId(r.data.id); setBody(r.data.body || '');
      if (!subject) setSubject(r.data.subject || '');
    } catch (ex) { setErr(ex?.response?.data?.detail || ex.message); }
    setBusy(false);
  };

  const send = async () => {
    if (!window.confirm(`Send "${subject}" to ${email.trim().toLowerCase()}?\n\nThis sends a real email now.`)) return;
    setBusy(true); setErr(null);
    try {
      await axios.put(`${API}/compose/draft/${draftId}`, {
        email: email.trim().toLowerCase(), name, subject, body,
      });
      const r = await axios.post(`${API}/compose/send`, { draft_id: draftId });
      setSent(r.data);
    } catch (ex) { setErr(ex?.response?.data?.detail || ex.message); }
    setBusy(false);
  };

  const ph = body.match(PLACEHOLDER);
  const cs = body.match(CS_MARKER);
  const blockedReason = ph
    ? `This draft still contains an unfilled placeholder (${ph[0]}…]). Fill it in or delete it.`
    : cs ? `This draft contains an internal marker (${cs[0]}) that must not be sent.`
      : !subject.trim() ? 'Add a subject line.'
        : !body.trim() ? 'The email is empty.'
          : who?.deliverability?.can_send === false
            ? `This address cannot receive mail: ${(who.deliverability.blockers || []).join(' ')}`
            : null;

  return (
    <div data-testid="compose" className="max-w-3xl">
      {/* RECIPIENT */}
      <div className="rounded-lg border border-hg-border bg-hg-panel p-4 mb-4">
        <div className="flex gap-2 flex-wrap">
          <input data-testid="compose-email" placeholder="email address…" value={email}
            onChange={(e) => { setEmail(e.target.value); setWho(null); setDraftId(null); setSent(null); }}
            onKeyDown={(e) => e.key === 'Enter' && lookUp()}
            className="flex-1 min-w-[240px] bg-hg-bg border border-hg-border rounded px-2 py-1 text-sm text-hg-text" />
          <input placeholder="name (optional)" value={name} onChange={(e) => setName(e.target.value)}
            className="w-40 bg-hg-bg border border-hg-border rounded px-2 py-1 text-sm text-hg-text" />
          <button data-testid="compose-lookup" onClick={lookUp} disabled={loadingWho}
            className="text-sm border border-hg-border rounded px-3 py-1 text-hg-text hover:bg-hg-bg">
            {loadingWho ? <Loader2 size={14} className="animate-spin" /> : <><Search size={14} className="inline mr-1" />Look up</>}
          </button>
        </div>
        <p className="text-xs text-hg-text-dim mt-2">
          Any address works — someone the Hub knows, or one it has never seen.
        </p>
      </div>

      {err && <div className="text-red-300 text-sm mb-3 border border-red-400/40 bg-red-400/10 rounded p-2">{err}</div>}

      {/* WHAT THE HUB KNOWS */}
      {who && (
        <div data-testid="compose-recipient-panel" className="rounded-lg border border-hg-border bg-hg-panel p-4 mb-4">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-semibold text-hg-text">
              {who.name || who.email}
              {!who.known_to_hub && <span className="ml-2 text-xs text-hg-text-dim italic">the Hub has never seen this address</span>}
            </h3>
            <span className="text-xs text-hg-text-dim">
              {who.days_since_last_contact == null ? 'no contact on record' : `${who.days_since_last_contact}d since last contact`}
            </span>
          </div>

          {who.excluded_note && (
            <p data-testid="compose-excluded" className="text-sm text-amber-300 border border-amber-400/40 bg-amber-400/10 rounded p-2 mb-2">
              <AlertTriangle size={13} className="inline mr-1" />{who.excluded_note}
            </p>
          )}

          <div className="text-sm mb-2">
            <span className="text-hg-text-dim">Subscriber status: </span>
            <strong className={who.deliverability.subscriber_status === 'active' ? 'text-hg-green'
              : who.deliverability.subscriber_status === 'unknown' ? 'text-amber-300' : 'text-red-300'}>
              {who.deliverability.subscriber_status}
            </strong>
            {who.deliverability.subscriber_status === 'unknown' && <HelpCircle size={12} className="inline ml-1 text-amber-300" />}
          </div>
          {(who.deliverability.blockers || []).map((b, i) => (
            <p key={i} className="text-sm text-red-300"><ShieldAlert size={13} className="inline mr-1" />{b}</p>
          ))}
          {(who.deliverability.notes || []).map((n, i) => (
            <p key={i} data-testid="compose-mailchimp-caveat" className="text-xs text-hg-text-dim mt-1 border-l-2 border-hg-border pl-2">{n}</p>
          ))}

          {!!who.events.length && (
            <details className="mt-3" open>
              <summary className="text-xs text-hg-text-dim cursor-pointer">History ({who.events.length})</summary>
              <ul className="mt-2 space-y-1">
                {who.events.slice(0, 12).map((e, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <span className="text-hg-text-dim text-xs w-24 shrink-0 pt-0.5">{day(e.at)}</span>
                    <DeliveryBadge state={e.delivery} />
                    <span className="flex-1 text-hg-text">{e.title}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* DRAFT */}
      {who && (
        <div className="rounded-lg border border-hg-border bg-hg-panel p-4 mb-4">
          <div className="flex gap-2 flex-wrap mb-3">
            <input data-testid="compose-intent" placeholder="What is this email for? One line."
              value={intent} onChange={(e) => setIntent(e.target.value)}
              className="flex-1 min-w-[240px] bg-hg-bg border border-hg-border rounded px-2 py-1 text-sm text-hg-text" />
            <button data-testid="compose-draft" onClick={draft} disabled={busy || !intent.trim()}
              className="text-sm border border-hg-green/50 text-hg-green rounded px-3 py-1 hover:bg-hg-green/10 disabled:opacity-40">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <><Sparkles size={14} className="inline mr-1" />Draft with AI</>}
            </button>
          </div>
          <input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)}
            className="w-full mb-2 bg-hg-bg border border-hg-border rounded px-2 py-1 text-sm text-hg-text" />
          <textarea data-testid="compose-body" rows={14} value={body} onChange={(e) => setBody(e.target.value)}
            placeholder="Write here, or draft with AI and edit."
            className="w-full bg-hg-bg border border-hg-border rounded px-2 py-2 text-sm text-hg-text font-mono" />
          <p className="text-xs text-hg-text-dim mt-1">Always editable. Nothing sends until you press Send.</p>

          {blockedReason && (
            <p data-testid="compose-blocked" className="mt-2 text-sm text-amber-300 border border-amber-400/40 bg-amber-400/10 rounded p-2">
              <AlertTriangle size={13} className="inline mr-1" />{blockedReason}
            </p>
          )}
          <button data-testid="compose-send" onClick={send} disabled={busy || !draftId || !!blockedReason}
            className="mt-3 text-sm border border-hg-green/50 text-hg-green rounded px-4 py-1.5 hover:bg-hg-green/10 disabled:opacity-40 disabled:cursor-not-allowed">
            <Send size={14} className="inline mr-1" />Send as Randall
          </button>
        </div>
      )}

      {/* RESULT — never "sent" on the record's own claim */}
      {sent && (
        <div data-testid="compose-result" className="rounded-lg border border-hg-border bg-hg-panel p-4">
          <div className="flex items-center gap-2 mb-2">
            <DeliveryBadge state={sent.delivery} />
            <strong className="text-hg-text text-sm">{sent.delivery_label}</strong>
          </div>
          <p className="text-xs text-hg-text-dim">campaign {sent.campaign_id}</p>
          {sent.delivery !== 'confirmed' && (
            <p className="text-sm text-amber-300 mt-2">
              This has left draft state at MailerLite but is NOT yet confirmed delivered.
              It will read confirmed once finished_at is set and stats.sent is above zero.
            </p>
          )}
          {sent.collision_note && <p className="text-sm text-amber-300 mt-2">{sent.collision_note}</p>}
        </div>
      )}
    </div>
  );
}
